// Écran d'authentification : overlay DOM au-dessus du canvas (le jeu reste 100 % canvas ;
// ici on veut de VRAIS champs HTML pour que les gestionnaires de mots de passe et le
// clavier natif fonctionnent). Le markup et les styles vivent dans public/index.html ;
// ce module le pilote : bascule d'onglets, soumission, affichage des erreurs serveur.
// Deux onglets : « Connexion » (pseudo, mot de passe) et « Créer un compte » (pseudo,
// mot de passe, code famille). Entrée soumet (soumission native du <form>). En cas de
// succès, on masque l'overlay et on rend la main à main.ts via onDone(me).
import type { MeResponse } from '../shared/api-types'
import { apiLogin, apiRegister } from './api'
import { mkById } from './util'

/** Références des éléments de l'overlay, résolues une fois (le markup est statique). */
interface Elements {
  root: HTMLElement
  tabLogin: HTMLButtonElement
  tabRegister: HTMLButtonElement
  formLogin: HTMLFormElement
  formRegister: HTMLFormElement
  loginPseudo: HTMLInputElement
  loginPassword: HTMLInputElement
  registerPseudo: HTMLInputElement
  registerPassword: HTMLInputElement
  registerCode: HTMLInputElement
  error: HTMLElement
}

let els: Elements | null = null
let wired = false
let onDone: ((me: MeResponse) => void) | null = null

/** Récupère un élément requis par id ou échoue clairement (markup index.html absent). */
const byId = mkById('Écran de connexion')

/** Résout (et mémorise) les éléments de l'overlay au premier appel. */
function resolve(): Elements {
  if (els) return els
  els = {
    root: byId('auth'),
    tabLogin: byId('auth-tab-login'),
    tabRegister: byId('auth-tab-register'),
    formLogin: byId('auth-form-login'),
    formRegister: byId('auth-form-register'),
    loginPseudo: byId('auth-login-pseudo'),
    loginPassword: byId('auth-login-password'),
    registerPseudo: byId('auth-register-pseudo'),
    registerPassword: byId('auth-register-password'),
    registerCode: byId('auth-register-code'),
    error: byId('auth-error'),
  }
  return els
}

/** Affiche un message d'erreur serveur (rouge doux) sous le formulaire. */
function showError(message: string): void {
  resolve().error.textContent = message
}

/** Efface le message d'erreur (changement d'onglet, nouvelle tentative). */
function clearError(): void {
  resolve().error.textContent = ''
}

type Tab = 'login' | 'register'

/** Bascule l'onglet actif : styles des onglets, formulaire visible, focus, erreur remise à zéro. */
function selectTab(tab: Tab): void {
  const e = resolve()
  const login = tab === 'login'
  e.tabLogin.classList.toggle('is-active', login)
  e.tabRegister.classList.toggle('is-active', !login)
  e.tabLogin.setAttribute('aria-selected', String(login))
  e.tabRegister.setAttribute('aria-selected', String(!login))
  e.formLogin.hidden = !login
  e.formRegister.hidden = login
  clearError()
  // Focus sur le premier champ du formulaire visible (confort clavier).
  ;(login ? e.loginPseudo : e.registerPseudo).focus()
}

/** Exécute une action d'auth (login/register) et gère succès / erreur / anti-double-clic. */
async function submit(action: () => Promise<MeResponse>, button: HTMLButtonElement): Promise<void> {
  clearError()
  button.disabled = true
  try {
    const me = await action()
    hideAuth()
    onDone?.(me)
  } catch (err) {
    // fetch qui rejette (panne réseau) ou Error(message serveur) : on affiche un texte lisible.
    showError(err instanceof Error && err.message ? err.message : 'Une erreur est survenue. Réessaie.')
  } finally {
    button.disabled = false
  }
}

/** Bouton de soumission d'un formulaire (toujours présent dans le markup). */
function submitButton(form: HTMLFormElement): HTMLButtonElement {
  return byId<HTMLButtonElement>(form.id + '-submit')
}

/** Branche les écouteurs une seule fois (onglets + soumissions). */
function wire(): void {
  if (wired) return
  wired = true
  const e = resolve()
  e.tabLogin.addEventListener('click', () => selectTab('login'))
  e.tabRegister.addEventListener('click', () => selectTab('register'))

  // Soumission native : Entrée dans un champ déclenche déjà submit ; on l'intercepte.
  // NB : on ne « trim » JAMAIS le mot de passe (des espaces peuvent être volontaires).
  e.formLogin.addEventListener('submit', (ev) => {
    ev.preventDefault()
    void submit(
      () => apiLogin(e.loginPseudo.value.trim(), e.loginPassword.value),
      submitButton(e.formLogin),
    )
  })
  e.formRegister.addEventListener('submit', (ev) => {
    ev.preventDefault()
    void submit(
      () => apiRegister(e.registerPseudo.value.trim(), e.registerPassword.value, e.registerCode.value.trim()),
      submitButton(e.formRegister),
    )
  })
}

/**
 * Affiche l'overlay d'authentification. onDone(me) est appelé APRÈS masquage de l'overlay,
 * une fois la session ouverte (connexion ou inscription réussie).
 */
export function showAuth(onDoneCallback: (me: MeResponse) => void): void {
  onDone = onDoneCallback
  wire()
  const e = resolve()
  e.root.hidden = false
  // Onglet initial : Connexion par défaut. ?auth=register le préselectionne (crochet de
  // dev pour les captures headless de l'onglet inscription ; sans effet en usage normal).
  selectTab(/[?&]auth=register\b/.test(location.search) ? 'register' : 'login')
}

/** Masque l'overlay (après succès, ou depuis main.ts). */
export function hideAuth(): void {
  if (els) els.root.hidden = true
}

/**
 * Vide les champs de saisie (à appeler à la déconnexion) : sur un Mac familial
 * partagé, l'enfant suivant ne doit pas retrouver le pseudo et le mot de passe
 * du compte précédent. Les gestionnaires de mots de passe re-remplissent d'eux-mêmes.
 */
export function resetAuthFields(): void {
  if (!els) return
  els.loginPseudo.value = ''
  els.loginPassword.value = ''
  els.registerPseudo.value = ''
  els.registerPassword.value = ''
  els.registerCode.value = ''
}
